/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { googleAnalytics } from '../services/googleAnalytics';
import type { User } from '@supabase/supabase-js';

interface UserContextType {
  user: User | null;
  loading: boolean;
  sessionChecked: boolean;
  refreshSession: () => Promise<void>;
  signOut: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({ 
  user: null, 
  loading: true, 
  sessionChecked: false,
  refreshSession: async () => {},
  signOut: async () => {}
});

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  useEffect(() => {
    // Check active sessions and set the user
    const initializeSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          // Session initialization error
          
          // Handle specific refresh token errors during initialization
          if (error.message?.includes('refresh_token_not_found') || 
              error.message?.includes('Invalid Refresh Token') ||
              error.message?.includes('Refresh Token Not Found')) {
            // Invalid refresh token detected during initialization, clearing session
            try {
              await supabase.auth.signOut();
            } catch (signOutError) {
              // Failed to sign out during initialization cleanup
              // Clear local storage manually if signOut fails
              if (typeof window !== 'undefined') {
                localStorage.removeItem('supabase.auth.token');
                sessionStorage.clear();
              }
            }
          }
          
          
          // Handle specific refresh token errors during initialization
          if (error.message?.includes('refresh_token_not_found') || 
              error.message?.includes('Invalid Refresh Token') ||
              error.message?.includes('Refresh Token Not Found')) {
            // Invalid refresh token detected during initialization, clearing session
            try {
              await supabase.auth.signOut();
            } catch (signOutError) {
              // Failed to sign out during initialization cleanup
              // Clear local storage manually if signOut fails
              if (typeof window !== 'undefined') {
                localStorage.removeItem('supabase.auth.token');
                sessionStorage.clear();
              }
            }
          }
          
          setSessionError(error.message);
          setUser(null);
        } else {
          setUser(session?.user ?? null);
          setSessionError(null);

          // Set Google Analytics user ID if user exists
          if (session?.user) {
            googleAnalytics.setUserId(session.user.id, {
              user_type: 'buyer', // Default, can be updated based on user profile
              verification_status: 'pending'
            });
          }
        }
      } catch (err) {
        // Failed to initialize session
        setSessionError('Failed to initialize session');
        setUser(null);
      } finally {
        setLoading(false);
        setSessionChecked(true);
      }
    };

    initializeSession();

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Auth state changed
      
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        setUser(session?.user ?? null);
        if (event === 'SIGNED_OUT') {
          setSessionError(null);
          // Clear any stale verification data
          sessionStorage.removeItem('verifiedProperty');
        }
        if (event === 'SIGNED_OUT') {
          setSessionError(null);
          // Clear any stale verification data
          sessionStorage.removeItem('verifiedProperty');
        }
      } else if (event === 'SIGNED_IN') {
        setUser(session?.user ?? null);
        setSessionError(null);

        // Track login event in Google Analytics
        if (session?.user) {
          googleAnalytics.setUserId(session.user.id);
          googleAnalytics.trackLogin('email');
        }
      } else if (event === 'USER_UPDATED') {
        setUser(session?.user ?? null);
      }
      
      setLoading(false);
      setSessionChecked(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Manual session refresh function
  const refreshSession = async () => {
    try {
      setLoading(true);
      
      // Check if we have a session first
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        console.warn('No active session to refresh');
        setUser(null);
        setSessionError('No active session');
        throw new Error('No active session');
      }
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Session refresh failed:', error);
        setSessionError(error.message);
        setUser(null);
        throw error;
      } else if (data.session) {
        console.debug('Session refreshed successfully');
        setUser(data.session.user);
        setSessionError(null);
      }
    } catch (err) {
      console.error('Error refreshing session:', err);
      setSessionError('Failed to refresh session');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      // Clear any verification data first
      sessionStorage.removeItem('verifiedProperty');
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        throw error;
      }
      
      setUser(null);
      setSessionError(null);
    } catch (err) {
      console.error('Error signing out:', err);
      // Clear local storage manually if signOut fails
      if (typeof window !== 'undefined') {
        localStorage.removeItem('supabase.auth.token');
        sessionStorage.clear();
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Refresh session periodically to prevent expiration (only if user exists)
  useEffect(() => {
    if (!user || !sessionChecked || sessionError) return;
    
    // Refresh session every 30 minutes
    const refreshInterval = setInterval(async () => {
      try {
        await refreshSession();
      } catch (err) {
        console.warn('Periodic session refresh failed:', err);
        // Don't throw here as it's a background operation
      }
    }, 30 * 60 * 1000); // 30 minutes
    
    return () => clearInterval(refreshInterval);
  }, [user, sessionChecked, sessionError]);

  return (
    <UserContext.Provider value={{ 
      user, 
      loading, 
      sessionChecked, 
      refreshSession, 
      signOut 
    }}>
      {children}
    </UserContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useUser = () => useContext(UserContext);